import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconArrowLeft, IconArrowRight, IconSend, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  MantineReactTable,
  type MRT_ColumnDef,
} from "mantine-react-table";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import VerificationStatusBadge from "../AdminStausBadge";

type CompanyPick = {
  id: string;
  legalName: string | null;
  gtid: string;
  country: string;
  status: string;
};

type ListCompanyApiRow = {
  id: string;
  name: string;
  gtid: string;
  verificationStatus: string;
  headquartersCountry?: string | null;
};

type HistoryRow = {
  id: string;
  companyA: { id: string; gtid: string; legalName: string };
  companyB: { id: string; gtid: string; legalName: string };
  adminNote: string | null;
  createdAt: string;
};

const PAGE_SIZE = 8;

const SKIP_REASON_LABELS: Record<string, string> = {
  NOT_FOUND: "company not found",
  BLOCKED: "company is blocked",
  NOT_VERIFIED: "company is not verified",
  ALREADY_EXISTS: "partner match already sent",
  ACTIVE_COLLABORATION: "collaboration already in progress between these companies",
};

function formatSkippedSummary(
  skipped: { gtid: string | null; reason: string }[] | undefined,
): string | null {
  if (!skipped?.length) return null;
  const parts = skipped.map((s) => {
    const label = SKIP_REASON_LABELS[s.reason] ?? s.reason;
    return s.gtid ? `${s.gtid} (${label})` : label;
  });
  return parts.join("; ");
}

function mapListCompanyRow(c: ListCompanyApiRow): CompanyPick {
  return {
    id: c.id,
    legalName: c.name,
    gtid: c.gtid,
    country: c.headquartersCountry?.trim() || "—",
    status: c.verificationStatus,
  };
}

function buildVerifiedCompanyFilter(search: string) {
  const q = search.trim();
  return {
    status: "VERIFIED" as never,
    ...(q ? { search: q } : {}),
  };
}

const SEARCH_PLACEHOLDER = "Search by company name, GTID, or country";

const COMPANY_PICKER_BASE_COLUMNS: MRT_ColumnDef<CompanyPick>[] = [
  { accessorKey: "gtid", header: "GTID", size: 110 },
  {
    accessorKey: "legalName",
    header: "Company",
    size: 200,
    Cell: ({ row }) => row.original.legalName || "—",
  },
  {
    accessorKey: "country",
    header: "Country",
    size: 130,
    Cell: ({ row }) => (
      <Text size="sm" lineClamp={1}>
        {row.original.country}
      </Text>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    size: 120,
    Cell: ({ row }) => <VerificationStatusBadge status={row.original.status} />,
  },
];

function CompanySinglePickerTable({
  title,
  excludeIds,
  selectedId,
  onSelect,
}: {
  title: string;
  excludeIds?: string[];
  selectedId: string | null;
  onSelect: (company: CompanyPick) => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [companies, setCompanies] = useState<CompanyPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const excludeSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, excludeIds]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await trpc.admin.listAll.query({
          page,
          limit: PAGE_SIZE,
          filter: buildVerifiedCompanyFilter(debouncedSearch),
        });
        if (cancelled) return;
        const rows = res.companies
          .map((c: ListCompanyApiRow) => mapListCompanyRow(c))
          .filter((c) => !excludeSet.has(c.id));
        setCompanies(rows);
      } catch (err) {
        if (!cancelled) handleTrpcError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, excludeSet]);

  const columns = useMemo<MRT_ColumnDef<CompanyPick>[]>(
    () => [
      ...COMPANY_PICKER_BASE_COLUMNS,
      {
        id: "select",
        header: "",
        size: 100,
        Cell: ({ row }) => (
          <Button
            size="xs"
            variant={selectedId === row.original.id ? "filled" : "light"}
            onClick={() => onSelect(row.original)}
          >
            {selectedId === row.original.id ? "Selected" : "Select"}
          </Button>
        ),
      },
    ],
    [selectedId, onSelect],
  );

  return (
    <Stack gap="md">
      <Text fw={600}>{title}</Text>
      <TextInput
        placeholder={SEARCH_PLACEHOLDER}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <MantineReactTable
          columns={columns}
          data={companies}
          enablePagination={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableSorting={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          mantineTableProps={{ highlightOnHover: true }}
        />
      )}
      <Group justify="space-between">
        <Button variant="default" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Previous
        </Button>
        <Button variant="default" onClick={() => setPage((p) => p + 1)}>
          Next page
        </Button>
      </Group>
    </Stack>
  );
}

function CompanyMultiPickerTable({
  title,
  excludeIds,
  selected,
  onToggle,
}: {
  title: string;
  excludeIds?: string[];
  selected: CompanyPick[];
  onToggle: (company: CompanyPick) => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [companies, setCompanies] = useState<CompanyPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const excludeSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
  const selectedIds = useMemo(() => new Set(selected.map((c) => c.id)), [selected]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, excludeIds]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await trpc.admin.listAll.query({
          page,
          limit: PAGE_SIZE,
          filter: buildVerifiedCompanyFilter(debouncedSearch),
        });
        if (cancelled) return;
        const rows = res.companies
          .map((c: ListCompanyApiRow) => mapListCompanyRow(c))
          .filter((c) => !excludeSet.has(c.id));
        setCompanies(rows);
      } catch (err) {
        if (!cancelled) handleTrpcError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, excludeSet]);

  const columns = useMemo<MRT_ColumnDef<CompanyPick>[]>(
    () => [
      ...COMPANY_PICKER_BASE_COLUMNS,
      {
        id: "select",
        header: "",
        size: 110,
        Cell: ({ row }) => {
          const isSelected = selectedIds.has(row.original.id);
          return (
            <Button
              size="xs"
              variant={isSelected ? "filled" : "light"}
              color={isSelected ? "red" : "primary"}
              onClick={() => onToggle(row.original)}
            >
              {isSelected ? "Remove" : "Add"}
            </Button>
          );
        },
      },
    ],
    [selectedIds, onToggle],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Text fw={600}>{title}</Text>
        <Badge size="lg" variant="light">
          {selected.length} selected
        </Badge>
      </Group>
      {selected.length > 0 && (
        <Group gap="xs">
          {selected.map((c) => (
            <Badge
              key={c.id}
              variant="outline"
              rightSection={
                <IconX
                  size={12}
                  style={{ cursor: "pointer" }}
                  onClick={() => onToggle(c)}
                  aria-label={`Remove ${c.gtid}`}
                />
              }
            >
              {c.gtid} · {c.country}
            </Badge>
          ))}
        </Group>
      )}
      <TextInput
        placeholder={SEARCH_PLACEHOLDER}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <MantineReactTable
          columns={columns}
          data={companies}
          enablePagination={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableSorting={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          mantineTableProps={{ highlightOnHover: true }}
        />
      )}
      <Group justify="space-between">
        <Button variant="default" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Previous
        </Button>
        <Button variant="default" onClick={() => setPage((p) => p + 1)}>
          Next page
        </Button>
      </Group>
    </Stack>
  );
}

export default function AdminPartnerMatchPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [companyA, setCompanyA] = useState<CompanyPick | null>(null);
  const [partnersB, setPartnersB] = useState<CompanyPick[]>([]);
  const [adminNote, setAdminNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await trpc.admin.listPartnerRecommendations.query({ page: 1, limit: 25 });
      setHistory(res.recommendations);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const togglePartnerB = useCallback((company: CompanyPick) => {
    setPartnersB((prev) => {
      const exists = prev.some((c) => c.id === company.id);
      if (exists) return prev.filter((c) => c.id !== company.id);
      return [...prev, company];
    });
  }, []);

  const historyColumns = useMemo<MRT_ColumnDef<HistoryRow>[]>(
    () => [
      {
        id: "pair",
        header: "Companies",
        Cell: ({ row }) => (
          <Text size="sm">
            {row.original.companyA.gtid} ↔ {row.original.companyB.gtid}
          </Text>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Sent",
        Cell: ({ row }) => new Date(row.original.createdAt).toLocaleString(),
      },
      {
        accessorKey: "adminNote",
        header: "Note",
        Cell: ({ row }) => row.original.adminNote || "—",
      },
    ],
    [],
  );

  const handleSend = async () => {
    if (!companyA || partnersB.length === 0) return;
    setSubmitting(true);
    try {
      const result = await trpc.admin.createPartnerRecommendation.mutate({
        primaryCompanyId: companyA.id,
        partnerCompanyIds: partnersB.map((c) => c.id),
        adminNote: adminNote.trim() || undefined,
      });

      const skippedCount = result.skipped?.length ?? 0;
      const createdCount = result.created?.length ?? 0;
      const skippedDetail = formatSkippedSummary(result.skipped);

      notifications.show({
        title: createdCount > 0 ? "Matches sent" : "No matches sent",
        message:
          skippedCount > 0
            ? createdCount > 0
              ? `${createdCount} partner match(es) saved. Skipped: ${skippedDetail}.`
              : `All selected partners were skipped. ${skippedDetail}.`
            : `${createdCount} partner match(es) saved successfully.`,
        color: createdCount > 0 ? "green" : skippedCount > 0 ? "orange" : "green",
      });

      setCompanyA(null);
      setPartnersB([]);
      setAdminNote("");
      setActiveStep(0);
      void loadHistory();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p="md">
      <Title order={2} mb="xs">
        Partner match
      </Title>
      <Text c="dimmed" mb="lg" size="sm">
        Select one Company A, then one or more Company B partners. All selected partners are recommended to Company A
        in a single send. Pairs with a collaboration already in progress (except declined) cannot receive a new
        partner match.
      </Text>

      <Paper p="lg" withBorder radius="md" mb="xl">
        <Stepper active={activeStep} onStepClick={setActiveStep} allowNextStepsSelect={false}>
          <Stepper.Step label="Company A" description="Primary company">
            <Box pt="md">
              <CompanySinglePickerTable
                title="Select Company A (receives the recommendations)"
                selectedId={companyA?.id ?? null}
                onSelect={(c) => {
                  setCompanyA(c);
                  setPartnersB((prev) => prev.filter((p) => p.id !== c.id));
                }}
              />
              <Group justify="flex-end" mt="md">
                <Button
                  rightSection={<IconArrowRight size={16} />}
                  disabled={!companyA}
                  onClick={() => setActiveStep(1)}
                >
                  Continue
                </Button>
              </Group>
            </Box>
          </Stepper.Step>

          <Stepper.Step label="Company B" description="Partner companies">
            <Box pt="md">
              {companyA && (
                <Badge mb="sm" variant="light" size="lg">
                  Company A: {companyA.gtid} — {companyA.legalName} ({companyA.country})
                </Badge>
              )}
              <CompanyMultiPickerTable
                title="Select one or more partner companies (Company B)"
                excludeIds={companyA ? [companyA.id] : undefined}
                selected={partnersB}
                onToggle={togglePartnerB}
              />
              <Group justify="space-between" mt="md">
                <Button
                  variant="default"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => setActiveStep(0)}
                >
                  Back
                </Button>
                <Button
                  rightSection={<IconArrowRight size={16} />}
                  disabled={partnersB.length === 0}
                  onClick={() => setActiveStep(2)}
                >
                  Review ({partnersB.length})
                </Button>
              </Group>
            </Box>
          </Stepper.Step>

          <Stepper.Step label="Send" description="Confirm & notify">
            <Box pt="md">
              <Stack gap="sm">
                <Text>
                  <strong>Company A:</strong> {companyA?.gtid} — {companyA?.legalName} ({companyA?.country})
                </Text>
                <Text fw={600}>Company B — {partnersB.length} partner(s):</Text>
                <Stack gap={4} pl="md">
                  {partnersB.map((c) => (
                    <Text key={c.id} size="sm">
                      • {c.gtid} — {c.legalName} ({c.country})
                    </Text>
                  ))}
                </Stack>
                <Textarea
                  label="Internal note (optional)"
                  description="Applied to all matches in this batch; not shown to companies."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.currentTarget.value)}
                  minRows={2}
                />
              </Stack>
              <Group justify="space-between" mt="md">
                <Button
                  variant="default"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => setActiveStep(1)}
                >
                  Back
                </Button>
                <Button
                  color="primary"
                  leftSection={<IconSend size={16} />}
                  loading={submitting}
                  onClick={() => void handleSend()}
                >
                  Send {partnersB.length} partner match{partnersB.length === 1 ? "" : "es"}
                </Button>
              </Group>
            </Box>
          </Stepper.Step>
        </Stepper>
      </Paper>

      <Title order={4} mb="sm">
        Recent recommendations
      </Title>
      {historyLoading ? (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      ) : (
        <MantineReactTable
          columns={historyColumns}
          data={history}
          enablePagination={false}
          enableColumnActions={false}
          enableSorting={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      )}
    </Box>
  );
}
